import type { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React, { useCallback } from 'react';
import styled from 'styled-components';

import { Container, Main } from '@seuk/design-system/atoms';
import { Breadcrumbs } from '@seuk/design-system/molecules';

import { CategorySection } from '../../../components/category-section';
import { PageHeader } from '../../../components/page-header';
import { getEntries, toDashed } from '../../../modules/page-help/utils';

const Sections = styled.div`
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(10)};
  margin-bottom: ${({ theme }) => theme.spacing(10)};
  ${({ theme }) => theme.breakpoints.up('lg')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

interface ArticleShort {
  title: string;
  slug: string;
  category: string;
}

interface CategoryPageProps {
  category: string;
  groupedArticles: Record<string, ArticleShort[]>;
}

const getCategories = async () => {
  const entries = await getEntries();

  const categories = entries.map((entry) => toDashed(entry.fields.category));

  return [...new Set(categories)];
};

const getGroupedArticles = async (category: string): Promise<Record<string, ArticleShort[]>> => {
  const entries = await getEntries();

  return entries.reduce((acc, entry) => {
    if (toDashed(entry.fields.category) === category) {
      const subcategory = entry.fields.subcategory;

      if (!acc[subcategory]) {
        acc[subcategory] = [];
      }
      acc[subcategory].push({
        title: entry.fields.title,
        category: entry.fields.category,
        slug: entry.fields.slug,
      });
    }
    return acc;
  }, {});
};

export default function CategoryGroupPage({ category, groupedArticles }: CategoryPageProps) {
  const title = `${category} – Shell Energy Business`;
  const trackClick = useCallback(() => {
    // placeholder for tracking
  }, []);
  const subcategories = Object.keys(groupedArticles);
  const breadcrumbItems = React.useMemo(() => {
    return [{ label: 'Shell Energy Business', href: '/help' }, { label: category }];
  }, [category]);

  return (
    <>
      <NextSeo title={title} noindex={true} />

      <Main>
        <Container>
          <Breadcrumbs items={breadcrumbItems} backHref="/help" />
        </Container>
        <PageHeader title={category} />
        <Container>
          <Sections>
            {subcategories.map((subcategory) => (
              <CategorySection
                label={subcategory}
                name={toDashed(subcategory)}
                key={subcategory}
                articles={groupedArticles[subcategory] ?? []}
                onArticleClick={trackClick}
              />
            ))}
          </Sections>
        </Container>
      </Main>
    </>
  );
}

export async function getStaticPaths() {
  const categories = await getCategories();

  return {
    paths: categories.map((category) => ({ params: { topCategoryName: category } })),
    fallback: false,
  };
}

export const getStaticProps: GetStaticProps<CategoryPageProps> = async (ctx) => {
  const topCategoryName = ctx.params?.topCategoryName?.toString() ?? '';

  const groupedArticles = await getGroupedArticles(topCategoryName);
  // extract category from article to not restore it from url
  const category = Object.values(groupedArticles)[0][0].category;

  return {
    props: { category, groupedArticles },
  };
};
